import React, {
  ChangeEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  HStack,
  Portal,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Filter20 } from '@carbon/icons-react';
import { any } from 'ramda';

import { useLocation } from '@reach/router';
import { VisibleTabFilterType } from 'src/features/explore/viewScenario/components/utils/utils';
import Topbar from '#coreComponents/Topbar';
import { Peer, usePageQuery } from '#core/PageQuery';
import PeerSelector, {
  getPeerSelectorData,
} from '#coreComponents/PeerSelector';
import QueryBuilder from '#coreComponents/QueryBuilder';
import { useQueryBuilder } from '#core/QueryBuilderContext';
import { EventType, useTracking, withEventTracking } from '#core/Tracking';
import { QueryBuilderContextValues } from '#core/QueryBuilderContext/components/QueryBuilderProvider';
import SettingsButton from '#core/Dashboard/components/SettingsButton';
import { useScenarioAndWurData } from '../../features/explore/viewScenario/providers/ScenarioDataProvider';
import DownloadDropdown, { DownloadURLs } from './DownloadDropdown';
import { useDataplusEmbargoData } from '../../features/core/Embargo/contexts/DataplusEmbargoContext';

export interface ProductTopbarProps {
  title: string;
  titleAcronym?: string;
  rank?: string;
  filter: {
    hasPeerSelector: boolean;
    hasQueryBuilder: boolean;
  };
  downloadUrls?: DownloadURLs;
  years?: (number | undefined)[];
  scenarioId?: string;
  currentTabIndex?: number;
  isFilterVisible?: VisibleTabFilterType;
  isNewWur3Methodology?: boolean;
  isWurReporterInterim?: boolean;
  isWurInterimTopBar?: boolean;
}

const ProductTopbar: React.FC<ProductTopbarProps> = ({
  title,
  titleAcronym,
  rank,
  filter,
  filter: { hasPeerSelector, hasQueryBuilder },
  downloadUrls,
  scenarioId,
  currentTabIndex,
  isFilterVisible,
  isNewWur3Methodology,
  isWurReporterInterim,
  years,
}) => {
  let peers: Peer[] = useMemo(() => [], []);
  let selectedPeers: Peer[] = [];
  let setSelectedPeers: (
    peers: Peer[] | ((peers: Peer[]) => Peer[]),
  ) => void = () => {};
  const {
    query: {
      peers: wurPeers,
      selectedPeers: wurSelectedPeers,
      selectedGroupAnalysisPeers,
      selectedYear,
      selectedQueryBuilderPeers,
      downloadInProgress,
      scoresRegion,
    },
    setSelectedYear,
    setSelectedPeers: wurSetSelectedPeers,
    setSelectedGroupAnalysisPeers,
    setSelectedQueryBuilderPeers,
  } = usePageQuery();

  const { availableSelectedYear } = useDataplusEmbargoData();

  const {
    peers: explorePeers,
    selectedPeers: exploreSelectedPeers,
    setSelectedPeers: exploreSetSelectedPeers,
  } = useScenarioAndWurData();

  const { pathname } = useLocation();

  const isPeerGroupAnalysisPage = pathname.includes('peer-group-analysis');

  const isExploreModule = pathname.includes('view-scenario');

  if (isExploreModule) {
    peers = explorePeers;
    selectedPeers = exploreSelectedPeers;
    setSelectedPeers = exploreSetSelectedPeers;
  } else if (isPeerGroupAnalysisPage) {
    peers = wurPeers;
    selectedPeers = selectedGroupAnalysisPeers;
    setSelectedPeers = setSelectedGroupAnalysisPeers;
  } else {
    peers = wurPeers;
    selectedPeers = wurSelectedPeers;
    setSelectedPeers = wurSetSelectedPeers;
  }

  const [filteredPeers, setFilteredPeers] = useState(
    hasQueryBuilder ? selectedQueryBuilderPeers : selectedPeers,
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const {
    config: queryBuilderConfig,
    filters: queryBuilderFilters,
    applyChanges: applyQueryBuilderChanges,
  } = useQueryBuilder();
  const [queryBuilderArgs, setQueryBuilderArgs] = useState<
    Maybe<QueryBuilderContextValues>
  >();

  const { trackEvent } = useTracking();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const peerSelectorData = useMemo(() => getPeerSelectorData(peers), [peers]);

  const shouldShowDownload = any(Boolean, Object.values(downloadUrls || {}));
  const shouldShowFilter = isExploreModule
    ? currentTabIndex &&
      isFilterVisible &&
      isFilterVisible[currentTabIndex] &&
      currentTabIndex >= 2
    : any(Boolean, Object.values(filter));
  const shouldShowTabs = hasPeerSelector && hasQueryBuilder;

  const isTabFitted = useBreakpointValue({ base: true, md: false });

  const onApply = () => {
    if (queryBuilderConfig && queryBuilderArgs) {
      applyQueryBuilderChanges(queryBuilderArgs);
    }

    if (!isPeerGroupAnalysisPage) {
      setSelectedPeers((prevValues) =>
        hasQueryBuilder ? prevValues : filteredPeers,
      );
    } else {
      setSelectedGroupAnalysisPeers(filteredPeers);
    }

    setSelectedQueryBuilderPeers((prevValues) =>
      hasQueryBuilder ? filteredPeers : prevValues,
    );
    setIsExpanded(false);
  };

  const onQueryBuilderChange = useCallback(
    (args: QueryBuilderContextValues) => {
      setQueryBuilderArgs(args);
    },
    [setQueryBuilderArgs],
  );

  const onPeerSelectorChange = useCallback(
    (selectedPeerIds) => {
      setFilteredPeers(
        peers.filter((peer) => selectedPeerIds.includes(peer.institutionId)),
      );
    },
    [setFilteredPeers, peers],
  );

  const onYearChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const year = parseInt(event.target.value, 10);
      setSelectedYear(year);
    },
    [setSelectedYear],
  );

  const onTopbarToggle = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const renderPeerSelector = () =>
    hasPeerSelector && (
      <PeerSelector
        data={peerSelectorData}
        defaultSelectedData={(hasQueryBuilder
          ? selectedQueryBuilderPeers
          : selectedPeers
        ).map((peer) => peer.institutionId)}
        onChange={onPeerSelectorChange}
      />
    );

  const renderQueryBuilder = () =>
    hasQueryBuilder &&
    queryBuilderConfig && (
      <QueryBuilder
        config={queryBuilderConfig}
        defaultSelectedData={queryBuilderFilters}
        onChange={onQueryBuilderChange}
      />
    );

  const downloadParams = {
    year: selectedYear,
    indicator: queryBuilderFilters.querySelectOne?.selectedItems[0],
    // Use selected years from QueryBuilder, fallback to all available years
    'years[]': queryBuilderFilters.selectedYears?.length
      ? queryBuilderFilters.selectedYears
      : queryBuilderConfig?.years,
    scenarioId,
    scoresRegion,
    isNewWur3Methodology,
    isWurReporterInterim,
    // Add QueryBuilder filter parameters (only include if there are selections)
    ...(queryBuilderFilters.selectedFilters?.subjects?.length
      ? { 'subjects[]': queryBuilderFilters.selectedFilters.subjects }
      : {}),
    ...(queryBuilderFilters.selectedFilters?.metrics?.length
      ? { 'metrics[]': queryBuilderFilters.selectedFilters.metrics }
      : {}),
    ...(queryBuilderFilters.selectedFilters?.cohorts?.length
      ? { 'cohorts[]': queryBuilderFilters.selectedFilters.cohorts }
      : {}),
  };

  const TopbarButtonWithTrackingEvent = withEventTracking<ButtonProps>(Button);

  return (
    <Box>
      {isExpanded && (
        <Box
          width={'100%'}
          height={'100vh'}
          backgroundColor={'rgba(0, 0, 0, 0.5)'}
          position={'absolute'}
          zIndex={10}
          top={0}
          left={0}
        />
      )}
      <Topbar
        fitSidebar
        withLogo
        dropdownRef={dropdownRef}
        title={title}
        titleAcronym={titleAcronym}
        wurRank={rank}
        wurYear={availableSelectedYear}
      >
        <Flex>
          <HStack justifyContent="end" flexGrow={1} spacing={2} px={2}>
            {!hasQueryBuilder && !!years?.length && !isExploreModule && (
              <Select
                w={32}
                variant="filled"
                bg="gray.800"
                color="gray.100"
                _hover={{ bg: 'gray.900' }}
                _focus={{ bg: 'gray.800' }}
                onChange={onYearChange}
                value={selectedYear}
                defaultValue={selectedYear}
                aria-label="Year"
              >
                {years?.map((year) => (
                  <Box key={year} as="option" bg="gray.100" color="gray.800">
                    {year}
                  </Box>
                ))}
              </Select>
            )}
            {shouldShowDownload && (
              <DownloadDropdown
                urls={downloadUrls!}
                params={downloadParams}
                loading={downloadInProgress}
              />
            )}
          </HStack>
          <HStack spacing={2} px={2}>
            {shouldShowFilter && (
              <TopbarButtonWithTrackingEvent
                eventData={() => ({
                  type: EventType.CLICK,
                  payload: {
                    id: `topbar-filter-${isExpanded ? 'close' : 'open'}`,
                    elementTagName: 'button',
                  },
                })}
                bg="white"
                _hover={{ bg: 'gray.100' }}
                _active={{ bg: 'gray.200' }}
                _focus={{ border: 'none' }}
                onClick={onTopbarToggle}
                borderStyle="solid"
                borderWidth={2}
                borderColor={isExpanded ? 'yellow.500' : 'transparent'}
              >
                <Text
                  as="span"
                  mr={2}
                  display={{ base: 'none', md: 'inline-block' }}
                >
                  Filter
                </Text>
                <Filter20 />
              </TopbarButtonWithTrackingEvent>
            )}
          </HStack>
          <SettingsButton />
        </Flex>
        {isExpanded && (
          <Box>
            <Portal containerRef={dropdownRef}>
              <Box
                position="absolute"
                width="80%"
                right="15px"
                bg="gray.800"
                borderRadius="md"
                zIndex={11}
              >
                <Flex
                  align="flex-start"
                  p={4}
                  mt={2}
                  borderColor="yellow.500"
                  w="100%"
                  direction="column"
                  maxH={{
                    base: 'calc(100vh - 203px)',
                    lg: 'calc(100vh - 141px)',
                  }}
                  overflow={{ base: 'auto', md: 'visible' }}
                >
                  {shouldShowTabs ? (
                    <Tabs
                      isFitted={isTabFitted}
                      variant="unstyled"
                      style={{ color: 'white' }}
                      w="100%"
                    >
                      <TabList>
                        <Tab
                          bg="gray.700"
                          border="none"
                          borderRadius="6px 0 0 6px"
                          _selected={{
                            color: 'white',
                            bg: 'yellow.500',
                            boxShadow: 'none',
                          }}
                          onClick={() => {
                            trackEvent({
                              type: EventType.CLICK,
                              payload: {
                                id: 'filter-tab-query-builder',
                                elementTagName: 'button',
                              },
                            });
                          }}
                        >
                          Query Builder
                        </Tab>
                        <Tab
                          bg="gray.700"
                          border="none"
                          borderRadius="0 6px 6px 0"
                          _selected={{
                            color: 'white',
                            bg: 'yellow.500',
                            boxShadow: 'none',
                          }}
                          onClick={() => {
                            trackEvent({
                              type: EventType.CLICK,
                              payload: {
                                id: 'filter-tab-peer-selector',
                                elementTagName: 'button',
                              },
                            });
                          }}
                        >
                          Peer Selector
                        </Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel p={0} pt={4}>
                          {renderQueryBuilder()}
                        </TabPanel>
                        <TabPanel p={0} pt={4}>
                          {renderPeerSelector()}
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  ) : (
                    <>
                      {renderPeerSelector()}
                      {renderQueryBuilder()}
                    </>
                  )}
                </Flex>
                <Flex justifyContent="flex-end" w="100%" py={5} px={10}>
                  <TopbarButtonWithTrackingEvent
                    eventData={() => ({
                      type: EventType.CLICK,
                      payload: {
                        id: 'topbar-close',
                        elementTagName: 'button',
                      },
                    })}
                    _hover={{ bg: 'gray.900' }}
                    _active={{ bg: 'gray.900' }}
                    _focus={{ bg: 'gray.900' }}
                    bg="white"
                    color="black"
                    onClick={() => setIsExpanded(false)}
                    borderStyle="solid"
                    borderWidth={2}
                    borderColor={'black'}
                    alignSelf="flex-end"
                    mr={5}
                    display={{ md: 'block', base: 'none' }}
                  >
                    <Text as="span">Close</Text>
                  </TopbarButtonWithTrackingEvent>
                  <TopbarButtonWithTrackingEvent
                    eventData={() => ({
                      type: EventType.CLICK,
                      payload: {
                        id: 'topbar-apply',
                        elementTagName: 'button',
                      },
                    })}
                    _hover={{ bg: 'gray.900' }}
                    _active={{ bg: 'gray.900' }}
                    _focus={{ bg: 'gray.900' }}
                    bg="white"
                    color="black"
                    onClick={onApply}
                    borderStyle="solid"
                    borderWidth={2}
                    borderColor={'black'}
                    flex={{ base: 1, md: 'none' }}
                  >
                    <Text as="span">Apply</Text>
                  </TopbarButtonWithTrackingEvent>
                </Flex>
              </Box>
            </Portal>
          </Box>
        )}
      </Topbar>
    </Box>
  );
};
export default ProductTopbar;
